import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useInRouterContext, useNavigate } from "react-router-dom";
import { Check, Code2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";

/**
 * CA5 (besluit Frank 1 november 2026): "bouw een app die ..." in de chat, altijd eerst deze kaart met Bevestig.
 *
 * Zelfde opbouw als `WorkerVoorstel`: de velden komen uit het opgeslagen voorstel (niet uit de tekst van het bericht),
 * de kaart krijgt een eenmalig token, en Bevestig stuurt de velden terug zoals ze op het scherm stonden. Na Bevestig
 * staan de repo en de werkruimte er, en opent Code de rit met deze opdracht (een keer).
 */
export default function AppVoorstel({ voorstelId }) {
  const { t } = useTaal();
  const inRouter = useInRouterContext();
  const [rij, setRij] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  // Na Bevestig meteen naar Code, waar de rit een keer start; buiten de router blijft het de link.
  const [naarCodeNu, setNaarCodeNu] = useState(false);
  const { token, vervallen, laatVervallen, reden } = useKaartToken(
    rij?.id ? `${API}/app-opdrachten/${rij.id}/toon` : "", Boolean(rij?.id && rij.status === "voorgesteld"));

  useEffect(() => {
    let levend = true;
    axios.get(`${API}/app-opdrachten/${voorstelId}`)
      .then((r) => { if (levend) setRij(r.data); })
      .catch((e) => { if (levend) setFout(e.response?.data?.detail || t("Dit voorstel kon niet worden geladen")); });
    return () => { levend = false; };
  }, [voorstelId]); // eslint-disable-line react-hooks/exhaustive-deps -- alleen bij een ander voorstel

  const bevestig = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/app-opdrachten/${rij.id}/uitvoeren`, {
        repo_naam: rij.repo_naam, opdracht: rij.opdracht, token,
      });
      setRij(r.data);
      toast.success(t("De repo {naam} en de werkruimte staan er; de agent begint in Code.", { naam: r.data.repo_naam }));
      setNaarCodeNu(true);
    } catch (e) {
      const tekst = e.response?.data?.detail || t("Het aanmaken is niet gelukt");
      setFout(tekst);
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  if (!rij) return fout ? <p className="text-xs text-destructive">{fout}</p> : null;
  const doel = `/code?app=${encodeURIComponent(rij.id)}`;
  const naarCode = inRouter
    ? <Link to={doel} className="underline ml-1" data-testid="app-naar-code">{t("Open in Code")}</Link>
    : <a href={doel} className="underline ml-1" data-testid="app-naar-code">{t("Open in Code")}</a>;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="app-voorstel">
      {inRouter && naarCodeNu && <GaNaar doel={doel} />}
      <div className="flex items-center gap-2 text-sm font-medium">
        <Code2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span data-testid="app-repo">{t("Repo {naam}, met een werkruimte erop", { naam: rij.repo_naam })}</span>
      </div>
      <p className="text-xs" data-testid="app-opdracht">{t("Opdracht voor de agent: {opdracht}", { opdracht: rij.opdracht })}</p>
      <p className="text-xs text-muted-foreground">
        {t("De agent werkt op een novamund-tak en stelt elke wijziging voor; jij past per bestand toe. Naar main gaat alleen via een pull request na jouw klik.")}
      </p>
      {rij.status === "voorgesteld" ? (
        <Button size="sm" onClick={bevestig} disabled={bezig || !token || vervallen}
                className={vervallen ? GRIJS : ""} data-testid="app-bevestig">
          {bezig && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          {t("Bevestig en maak de repo en de werkruimte")}
        </Button>
      ) : (
        <p className="text-xs flex items-center gap-1" data-testid="app-gemaakt">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          {rij.status === "gestart" ? t("Aangemaakt; de agent is in Code begonnen.") : t("Aangemaakt.")}
          {naarCode}
        </p>
      )}
      {(fout || reden) && <p className="text-xs text-destructive">{fout || reden}</p>}
    </div>
  );
}

function GaNaar({ doel }) {
  const navigate = useNavigate();
  useEffect(() => { navigate(doel); }, [doel, navigate]);
  return null;
}
